import { useEffect, useState } from 'react';
import { addInvite, listInvites, removeInvite, type Invite } from '../lib/api';
import { APP_NAME } from '../lib/brand';
import { useStore } from '../lib/store';

/**
 * Hands over the link, since adding someone to the allowlist deliberately sends
 * nothing. On a phone this opens the OS share sheet, which puts the link into
 * whichever thread you already talk to them in — faster and more reliable than
 * an email that has to survive a spam filter.
 */
function ShareLink({ guildName }: { guildName: string }) {
  const [copied, setCopied] = useState(false);
  const url = window.location.origin;
  // The DOM types declare navigator.share unconditionally, but it is absent on
  // desktop browsers, so this has to be a real runtime check.
  const canShare = 'share' in navigator;

  async function share() {
    const payload = {
      title: `${APP_NAME} · ${guildName}`,
      text: `Come log games with ${guildName}:`,
      url,
    };
    if (canShare) {
      try {
        await navigator.share(payload);
        return;
      } catch {
        // Cancelled, or share is unavailable in this context — fall through.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked; the URL is in the address bar anyway.
    }
  }

  return (
    <button className="btn ghost block" style={{ marginTop: 10 }} onClick={() => void share()}>
      {copied ? 'Link copied' : canShare ? 'Share the link' : 'Copy the link'}
    </button>
  );
}

/**
 * Guild access is an allowlist, and inviting someone sends them nothing. The UI
 * says so plainly, because a button labelled "invite" that quietly does not send
 * anything is a bug in the copy even when the code is correct.
 *
 * Sending real mail would mean SES, which starts in a sandbox where every
 * recipient has to be verified before it will deliver — more setup than a group
 * of three needs to swap a URL.
 *
 * The gate is real: the PreSignUp trigger rejects any Google account whose
 * verified address is not on this list, so an uninvited stranger with the URL
 * gets a refusal, not a login.
 */
export function Invites() {
  const { state } = useStore();
  const guildName = state.settings.guildName;
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The address whose revoke button has been armed, if any. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = () =>
    listInvites()
      .then(setInvites)
      .catch(() => setInvites([]));

  useEffect(() => {
    void load();
  }, []);

  async function invite() {
    const trimmed = email.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await addInvite(trimmed);
      setEmail('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not add that invite');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Take an invitation back.
   *
   * Two taps rather than one. The consequence is not visible from here — an
   * address quietly leaving the allowlist looks identical to nothing having
   * happened — and it is the difference between somebody being able to sign in
   * and not, which is a bad thing to do with a mis-tap on a phone.
   *
   * The server decides who may: anyone can take back an invite they sent, which
   * is undoing their own action, and revoking somebody else's needs the leader
   * role. Nothing here has to know that; a refusal comes back as a message.
   */
  async function revoke(target: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await removeInvite(target);
      setConfirming(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not take that invite back');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Only the ones still outstanding.
   *
   * An invite is a door held open, and once somebody has walked through it the
   * row is describing history — it sat above the member it had already let in,
   * which read as two states for one person. The allowlist entry stays on the
   * server either way; it has to, or their next sign-in would be refused.
   */
  const joined = new Set(
    state.pilots.map((p) => p.email?.toLowerCase()).filter((e): e is string => Boolean(e)),
  );
  const pending = (invites ?? []).filter((inv) => !joined.has(inv.email.toLowerCase()));

  return (
    <>
      <div className="section-head">
        <h2>Guild access</h2>
        {invites && pending.length > 0 && <span className="hint num">{pending.length}</span>}
      </div>

      <div className="panel" style={{ marginBottom: 10 }}>
        <label className="field" style={{ marginBottom: 10 }}>
          <span>Invite to {guildName}</span>
          <input
            className="input"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            placeholder="them@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void invite();
            }}
          />
        </label>
        <button className="btn primary block" disabled={!email.trim() || busy} onClick={() => void invite()}>
          {busy ? <span className="spinner" /> : 'Invite to guild'}
        </button>
        {error && <p className="error">{error}</p>}
        <p className="hint" style={{ marginTop: 10 }}>
          This opens the door — it does <strong>not</strong> send a message. Share the link
          yourself, then they tap “Continue with Google”. The address you invite has to
          match the Google account they sign in with.
        </p>
        <ShareLink guildName={guildName} />
      </div>

      {pending.length > 0 && (
        <div className="list">
          {pending
            .slice()
            .sort((a, b) => a.email.localeCompare(b.email))
            .map((inv) => (
              <div className="list-item" key={inv.email}>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {inv.email}
                  </div>
                  <div className="sub">invited by {inv.invitedBy}</div>
                </div>
                {/* Armed by the first tap, done by the second. Tapping anything
                    else disarms it, so there is always a way out that is not
                    the destructive one. */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {confirming === inv.email ? (
                    <>
                      <button
                        className="invite-action"
                        disabled={busy}
                        onClick={() => setConfirming(null)}
                      >
                        Keep
                      </button>
                      <button
                        className="invite-action danger"
                        disabled={busy}
                        onClick={() => void revoke(inv.email)}
                      >
                        {busy ? <span className="spinner" /> : 'Revoke'}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="pip">waiting</span>
                      <button
                        className="invite-action"
                        onClick={() => setConfirming(inv.email)}
                        aria-label={`take back the invite to ${inv.email}`}
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </span>
              </div>
            ))}
        </div>
      )}
    </>
  );
}
